import type { TFunction } from "i18next";

import {
  BriefcaseIcon,
  ChartSquareIcon,
  CoinsHandIcon,
  DocumentWhiteIcon,
  MegaphoneIcon,
  UsersArrowIcon,
  UserTickIcon,
} from "@partners/ui";

import type { NavLinkData } from "./AuthenticatedBaseHeader";

type NavLinksOptions = {
  showExportReport?: boolean;
  showPerformanceReport?: boolean;
  referralLinks?: boolean;
  showMasterSubIbReport?: boolean;
};

export const getNavLinksOverviewOnly = (tFn: TFunction): NavLinkData[] => [
  {
    name: "Overview",
    to: "/",
    label: tFn("global_header_overview_link"),
    icon: <ChartSquareIcon key="overview-nav-icon" />,
    segmentEvent: "Overview",
  },
];

export const getNavLinks = (
  tFn: TFunction,
  {
    showExportReport = false,
    referralLinks = false,
    showPerformanceReport = false,
    showMasterSubIbReport = false,
  }: NavLinksOptions = {},
): NavLinkData[] => {
  const links: NavLinkData[] = [
    {
      name: "Overview",
      to: "/",
      label: tFn("global_header_overview_link"),
      icon: <ChartSquareIcon key="overview-nav-icon" />,
      segmentEvent: "Overview",
    },
    {
      name: "Marketing",
      to: "/marketing",
      label: referralLinks
        ? tFn("global_header_marketing_tool_link")
        : tFn("global_header_marketing_link"),
      icon: <MegaphoneIcon key="marketing-nav-icon" />,
      segmentEvent: referralLinks ? "Marketing tools" : "Marketing",
    },
    {
      name: "Leads",
      to: "/leads",
      label: tFn("global_header_leads_link"),
      icon: <UsersArrowIcon key="leads-nav-icon" />,
      segmentEvent: "Leads",
    },
    {
      name: "Clients",
      to: "/clients",
      label: tFn("global_header_clients_link"),
      icon: <UserTickIcon key="clients-nav-icon" />,
      segmentEvent: "Clients",
    },
    ...(showMasterSubIbReport
      ? [
          {
            name: "IB network" as const,
            // TODO: IB network is meant to have its own 2-item sub-nav
            // ("Your Master IB" / "Your Sub IBs" — see
            // apps/portal/src/routes/_authenticated/_IBComplete/ib-network/),
            // but there's no dropdown/sub-nav pattern in this header yet, so
            // this points straight at "Your Sub IBs" with its default tab
            // pinned explicitly (`search.tab`) rather than relying on
            // SubIBReportPage's own internal default. Revisit both the
            // hardcoded tab and this single link once the sub-nav is
            // designed/built and IB network's real default entry point
            // (Master IB vs Sub IBs) is decided.
            to: "/ib-network/your-sub-ibs",
            search: { tab: "shared-rebate-program" },
            label: tFn("global_header_ib_network_link"),
            icon: (
              <BriefcaseIcon
                key="ib-network-nav-icon"
                className="[&_path]:stroke-current"
              />
            ),
            segmentEvent: "IB network",
          },
        ]
      : []),
    ...(showPerformanceReport
      ? [
          {
            name: "Performance" as const,
            to: "/performance",
            label: tFn("global_header_performance_link"),
            icon: <ChartSquareIcon key="performance-nav-icon" />,
            segmentEvent: "Performance",
          },
        ]
      : []),
    {
      name: "Earnings",
      to: "/earnings",
      label: tFn("global_header_earnings_link"),
      icon: <CoinsHandIcon key="earnings-nav-icon" />,
      segmentEvent: "Earnings",
    },
  ];

  if (showExportReport) {
    links.push({
      name: "Export report",
      to: "/export-report",
      label: "Export report",
      icon: <DocumentWhiteIcon key="export-report-nav-icon" />,
      segmentEvent: "Export report",
    });
  }

  return links;
};
